import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EngineeringIcon from "@mui/icons-material/Engineering";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import BlurLinearIcon from "@mui/icons-material/BlurLinear";
import HeatPumpIcon from "@mui/icons-material/HeatPump";
import CalculateIcon from "@mui/icons-material/Calculate";
import CoilsIcon from "@mui/icons-material/Animation";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import { AhuType } from "../../models/unitType";
import useTheme from "@mui/material/styles/useTheme";

export const Steps = {
  UNIT_INFO: "unit-info",
  TYPE_OF_UNIT: "type-of-unit",
  OPERATING_POINT: "operating-point",
  CONNECTION: "connection",
  FILTERING: "filtering",
  FAN: "fan",
  CALCULATION: "calculation",
  HEAT_RECOVERY: "heat-recovery",
  COILS: "coils",
  HEAT_PUMP: "heat-pump",
};

export const useSteps = (ahuType = 1, options = {}) => {
  const { coils } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStepRef = useRef(
    parseInt(searchParams.get("step") || "0", 10),
  );
  const [activeStepIndex, setActiveStepIndex] = useState(
    initialStepRef.current,
  );
  const [steps, setSteps] = useState([]);
  const { t } = useTranslation();
  const theme = useTheme();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const stepsConfiguration = getStepsConfiguration(ahuType, coils);
    const visibleSteps = stepsConfiguration
      .filter((step) => step.visible)
      .map((step, index) => ({ ...step, step: index }));

    setSteps(visibleSteps);

    if (!isInitializedRef.current) {
      const clampedIndex = Math.min(
        Math.max(0, initialStepRef.current),
        Math.max(0, visibleSteps.length - 1),
      );
      setActiveStepIndex(clampedIndex);
      isInitializedRef.current = true;
    } else {
      setActiveStepIndex((prev) =>
        Math.min(prev, Math.max(0, visibleSteps.length - 1)),
      );
    }
  }, [ahuType, coils]);

  // Sync activeStepIndex to URL search params
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", String(activeStepIndex));
        return next;
      },
      { replace: true },
    );
  }, [activeStepIndex, setSearchParams]);

  const nextStep = () => {
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex(activeStepIndex + 1);
    }
  };

  const previousStep = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1);
    }
  };

  const completeStep = (stepId) => {
    const updatedSteps = steps.map((step) => {
      if (step.id === stepId) {
        return { ...step, completed: true };
      }
      return step;
    });
    setSteps(updatedSteps);
  };

  const getStepsConfiguration = (ahuType, coils) => {
    const isLinePrime = ahuType === AhuType.LINE_PRIME;

    return [
      {
        id: Steps.UNIT_INFO,
        label: t("mainform.info"),
        completed: false,
        visible: true,
        icon: <AssignmentIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.TYPE_OF_UNIT,
        label: t("mainform.typeOfUnit"),
        completed: false,
        visible: true,
        icon: <EngineeringIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.OPERATING_POINT,
        label: t("mainform.operatingpoint"),
        completed: false,
        visible: true,
        icon: <GpsFixedIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.CONNECTION,
        label: t("mainform.connection"),
        completed: false,
        visible: isLinePrime,
        icon: (
          <SettingsInputComponentIcon
            sx={{ color: theme.palette.secondary.main }}
          />
        ),
      },
      {
        id: Steps.HEAT_RECOVERY,
        label: t("mainform.heatRecovery"),
        completed: false,
        visible:
          ahuType === AhuType.ENERGY_PRIME_STANDARD ||
          ahuType === AhuType.ENERGY_PRIME_GENIUS,
        icon: <HeatPumpIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.HEAT_PUMP,
        label: t("mainform.heatPump.title"),
        completed: false,
        visible: ahuType === AhuType.ENERGY_PRIME_GENIUS,
        icon: <HeatPumpIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.COILS,
        label: t("mainform.coils"),
        completed: false,
        visible: isLinePrime ? coils === "cooler" : true,
        icon: <CoilsIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.FILTERING,
        label: t("mainform.filtering"),
        completed: false,
        visible: true,
        icon: <BlurLinearIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.FAN,
        label: t("mainform.fan"),
        completed: false,
        visible: true,
        icon: <HeatPumpIcon sx={{ color: theme.palette.secondary.main }} />,
      },
      {
        id: Steps.CALCULATION,
        label: t("mainform.calculation"),
        completed: false,
        visible: true,
        icon: <CalculateIcon sx={{ color: theme.palette.secondary.main }} />,
      },
    ];
  };

  return {
    steps,
    activeStep: steps[activeStepIndex],
    activeStepIndex,
    nextStep,
    previousStep,
    completeStep,
  };
};
